/**
 * Build: talk to Mo (left) and watch the agent take shape (right).
 * Left column top to bottom: conversation manager, transcript, dock. The dock holds
 * the single focus point (what Mo does right now, its questions) directly
 * above the input.
 * Opening AI Studio always starts a new conversation (old ones are in the conversation
 * list), unless Mo is still working or waiting for the owner: then that conversation
 * opens. A new conversation is the start page: Mo's welcome (WelcomeMessage), then, once
 * its question is written, the large input and right after it the three questions. On the first message the message is at the top at once and only the input
 * glides down to the dock; the welcome stays above, a scroll up away.
 * The "Your agent" panel starts collapsed to a rail and opens itself when Mo changes
 * the agent; the owner's open/closed choice is kept per agent.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate, faBell, faBoxOpen, faCalendarCheck, faChartLine, faClock, faComments, faFlask, faGlobe, faHeadset,
  faLanguage, faListCheck, faMasksTheater, faPhone, faPuzzlePiece, faTableColumns, faUserPlus, faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import StudioComposer from '../StudioComposer';
import useAiCredits, { creditsOn } from '../../hooks/useAiCredits';
import { canWrite, guardWrite } from '../../utils/permissions';
import AgentPanel, { PANEL_TAB_EVENT } from './AgentPanel';
import { DRAFT_EVENT } from './AppsTab';
import BuildFocus, { MoWorking } from './BuildStatusPanel';
import BuildTranscript, { groupItems } from './BuildTranscript';
import ConversationPicker from './ConversationPicker';
import ConversationSidebar, { useConversationSide } from './ConversationSidebar';
import { CreditsChip, MoLevelMenu } from './CreditLevels';
import { studioUrl } from './studioApi';
import { useStudioFigures } from './studioData';
import WelcomeMessage from './WelcomeMessage';

const STARTERS = ['winery', 'practice', 'shop', 'craft'];
// Mo's ideas: what Mo can do beyond building (tests, analysis, live chat, channels, knowledge, team), in a new
// order on every visit (Robert, 16.10.2026). The first three are the buttons under the input, the others are
// written into the empty input one after another, after its usual line, so no idea shows twice at once.
// Ideas 1, 4, 7, 8, 10, 13, 14 and 15 were taken out, 16 to 23 added.
const IDEAS = [
  [2, faMasksTheater], [3, faHeadset], [5, faChartLine], [6, faGlobe], [9, faCalendarCheck], [11, faBoxOpen],
  [12, faUserPlus], [16, faListCheck], [17, faLanguage], [18, faComments], [19, faBell], [20, faArrowsRotate],
  [21, faPuzzlePiece], [22, faPhone], [23, faClock],
].map(([n, icon]) => ({ key: `agent_studio_composer_idea_${n}`, icon }));
const IDEA_BUTTONS = 3;
// idea 17 names three languages to test the agent in, picked anew on every visit and never the owner's own;
// the browser writes their names in the owner's language ("Türkisch", "turc", "turco"). Never Russian (Robert, 17.10.2026).
const TEST_LANGUAGES = ['en', 'de', 'es', 'fr', 'it', 'pt', 'nl', 'pl', 'tr', 'uk', 'ar', 'el', 'sv', 'cs', 'ro', 'zh', 'ja'];
const NAME_LOCALE = { pt: 'pt-PT' }; // the Portuguese texts are European Portuguese
function shuffled(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
export function someLanguages(uiLanguage, count = 3) {
  const own = (uiLanguage || 'en').slice(0, 2);
  const picked = shuffled(TEST_LANGUAGES.filter((l) => l !== own)).slice(0, count);
  try {
    const locale = NAME_LOCALE[own] || own;
    const names = new Intl.DisplayNames([locale], { type: 'language' });
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(picked.map((l) => names.of(l)));
  } catch (_) {
    return picked.join(', ');
  }
}

function readFlag(key) {
  try { return localStorage.getItem(key) === '1'; } catch (_) { return false; }
}
function writeFlag(key, on) {
  try { localStorage.setItem(key, on ? '1' : '0'); } catch (_) { /* storage unavailable */ }
}

function readDraft(key) {
  try { return localStorage.getItem(key) || ''; } catch (_) { return ''; }
}
function writeDraft(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (_) { /* storage unavailable */ }
}

export default function BuildView({ botId, spec, setSpec, builder, knowledgeVersion, goTest }) {
  const { t } = useTranslation();
  const [pane, setPane] = useState('chat');
  const [error, setError] = useState('');
  const { state, items, conversationId } = builder;
  const running = !!state?.running;
  const runningHere = running && (!state?.run_conversation_id || state.run_conversation_id === conversationId);
  const { groups, liveSteps } = groupItems(items, runningHere);
  const hasAgent = !!(spec?.skills?.length || state?.conversations?.length);
  // Mo's welcome heads every conversation (typed out only on a fresh start page, see WelcomeMessage)
  const empty = !!state && items.length === 0;
  const showWelcome = !!state;
  const welcomeKey = String(conversationId);
  const [welcomeDone, setWelcomeDone] = useState(null); // the welcome that is complete
  const figures = useStudioFigures(botId);
  const settled = useArrival(builder);
  // the start page; also while arrival settles, so the input does not jump there from the dock
  const hero = !settled || empty;
  // the input waits for the arrival and, on the start page, for the welcome to be complete
  const veiled = !settled || (hero && showWelcome && welcomeDone !== welcomeKey);

  const panelKey = `agentStudio.panelOpen.${botId}`;
  const [panelOpen, setPanelOpen] = useState(() => readFlag(panelKey));
  const [agentDot, setAgentDot] = useState(false);
  const togglePanel = (open) => {
    setPanelOpen(open);
    writeFlag(panelKey, open);
  };
  // Mo changed the agent: open the panel (not stored: the owner's own choice stays)
  const onChanged = () => {
    setPanelOpen(true);
    if (pane !== 'agent') setAgentDot(true);
  };
  const showPane = (p) => {
    setPane(p);
    if (p === 'agent') setAgentDot(false);
  };
  // a tab of the panel was asked for (the success score's actions): show the panel
  useEffect(() => {
    const open = () => { togglePanel(true); showPane('agent'); };
    window.addEventListener(PANEL_TAB_EVENT, open);
    return () => window.removeEventListener(PANEL_TAB_EVENT, open);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = async () => {
    if (!items.length) return; // already in an empty new conversation (as in ChatGPT: nothing happens)
    if (!guardWrite()) return;
    setError('');
    try {
      await builder.newConversation();
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  // the welcome hands Mo a request (the success score's "Help me fix it")
  const ask = async (text) => {
    if (!guardWrite()) return;
    setError('');
    try {
      await builder.send(text);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  // an app prepared a request for Mo's input (AppsTab): show the conversation on mobile
  useEffect(() => {
    const toChat = () => setPane('chat');
    window.addEventListener(DRAFT_EVENT, toChat);
    return () => window.removeEventListener(DRAFT_EVENT, toChat);
  }, []);

  const removeConversations = async (ids) => {
    setError('');
    try {
      await builder.deleteConversations(ids);
    } catch (e) {
      setError(e.message || String(e));
    }
  };
  const [sideOpen, toggleSide] = useConversationSide();
  const renameConversation = async (id, title) => {
    setError('');
    try {
      return await builder.renameConversation(id, title);
    } catch (e) {
      setError(e.message || String(e));
      return null;
    }
  };

  return (
    <div className="as-build has-side">
      {/* the conversations with Mo, as in ChatGPT (wide screens; narrow ones keep the picker in the chat head) */}
      <ConversationSidebar
        title={t('agent_studio_build_conversations')}
        conversations={state?.conversations || []}
        currentId={conversationId}
        runningId={running ? state?.run_conversation_id : null}
        onOpen={builder.openConversation}
        onNew={startNew}
        onDelete={(id) => removeConversations([id])}
        onRename={renameConversation}
        onSearch={builder.searchConversations}
        open={sideOpen}
        onToggle={toggleSide}
      />
      <div className="as-mobile-switch">
        <div className="as-seg" style={{ width: '100%' }}>
          <button type="button" className={pane === 'chat' ? 'is-active' : ''} onClick={() => showPane('chat')} style={{ flex: 1 }}>
            <FontAwesomeIcon icon={faComments} /> {t('agent_studio_tab_conversation')}
          </button>
          <button type="button" className={pane === 'agent' ? 'is-active' : ''} onClick={() => showPane('agent')}
            style={{ flex: 1, position: 'relative' }}>
            <FontAwesomeIcon icon={faTableColumns} /> {t('agent_studio_your_agent')}{spec ? ` (${spec.skills.length})` : ''}
            {agentDot && <span className="as-tab-dot" title={t('agent_studio_panel_changed')} />}
          </button>
        </div>
      </div>

      <div className={`as-build-chat ${hero ? 'is-hero' : ''} ${pane === 'chat' ? '' : 'is-hidden-mobile'}`}>
        <div className="as-chat-head">
          <ConversationPicker
            conversations={state?.conversations || []}
            currentId={conversationId}
            runningId={running ? state?.run_conversation_id : null}
            onOpen={builder.openConversation}
            onNew={startNew}
            onDelete={(id) => removeConversations([id])}
            onDeleteMany={removeConversations}
            newDisabled={false}
            menuTitle={t('agent_studio_build_conversations')}
          />
          {error && <span className="as-small as-warn-text">{t('agent_studio_action_failed', { error })}</span>}
        </div>

        {/* always mounted, so the first message of a new conversation is pinned too */}
        {/* nothing until arrival settled, so the previous conversation does not flash up */}
        <BuildTranscript builder={builder} groups={settled ? groups : []} calm={hero}
          live={settled ? <MoWorking builder={builder} liveSteps={liveSteps} /> : null}
          lead={settled && showWelcome ? (
            <WelcomeMessage key={welcomeKey} hasAgent={hasAgent} figures={figures} onAsk={ask} fresh={empty}
              onDone={() => setWelcomeDone(welcomeKey)} />
          ) : null} />

        <Dock botId={botId} builder={builder} liveSteps={liveSteps} running={running}
          canTest={!!spec?.skills?.length} goTest={goTest} hero={hero} veiled={veiled}
          hasAgent={hasAgent} showQuestions={welcomeDone === welcomeKey} />
      </div>

      <aside className={`as-build-agent ${panelOpen ? '' : 'is-collapsed'} ${pane === 'agent' ? '' : 'is-hidden-mobile'}`}>
        <AgentPanel botId={botId} spec={spec} setSpec={setSpec} knowledgeVersion={knowledgeVersion} building={running}
          collapsed={!panelOpen} onExpand={() => togglePanel(true)} onCollapse={() => togglePanel(false)}
          onChanged={onChanged} onAskMo={(text) => { setPane('chat'); return ask(text); }} />
      </aside>
    </div>
  );
}

/**
 * Opening AI Studio: a new conversation, unless Mo is working (then the conversation it
 * works in) or waits for the owner's answer in the open one. True once that is settled.
 */
function useArrival(builder) {
  const done = useRef(false);
  const [settled, setSettled] = useState(false);
  const { state, items, pending, conversationId, newConversation, openConversation } = builder;
  useEffect(() => {
    if (done.current || !state) return;
    done.current = true;
    const finish = () => setSettled(true);
    if (state.running) {
      const runConv = state.run_conversation_id;
      if (runConv && runConv !== conversationId) {
        Promise.resolve().then(() => openConversation(runConv)).catch(() => { /* stays where it is */ }).finally(finish);
      } else finish();
      return;
    }
    if (!items.length || pending.length || !canWrite()) { finish(); return; }
    Promise.resolve().then(newConversation).catch(() => { /* the last conversation stays open */ }).finally(finish);
  }, [state, items, pending, conversationId, newConversation, openConversation]);
  return settled;
}

function Dock({ botId, builder, liveSteps, running, canTest, goTest, hero, veiled, hasAgent, showQuestions }) {
  const { t, i18n } = useTranslation();
  const key = `agentStudio.draft.${botId}`;
  const [text, setText] = useState(() => readDraft(key));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ideas] = useState(() => shuffled(IDEAS));
  const [languages] = useState(() => someLanguages(i18n.language));
  const idea = (k) => t(k, { languages });
  const questions = ideas.slice(0, IDEA_BUTTONS).map(({ key: k, icon }) => ({ key: k, icon, text: idea(k) }));
  const ref = useRef(null);
  const box = useRef(null);
  const last = useRef({ hero, veiled, top: null });

  useEffect(() => setText(readDraft(key)), [key]);
  useEffect(() => writeDraft(key, text), [key, text]);
  // a request prepared elsewhere (an app in the panel) lands in the input
  useEffect(() => {
    const reload = () => {
      setText(readDraft(key));
      setTimeout(() => ref.current?.focus(), 0);
    };
    window.addEventListener(DRAFT_EVENT, reload);
    return () => window.removeEventListener(DRAFT_EVENT, reload);
  }, [key]);
  // the input fades in (start page: once the welcome is complete) and takes the focus
  useEffect(() => { if (!veiled) ref.current?.focus(); }, [veiled]);
  // AI credits: Mo's level and the credits left sit under the input; after every Mo run all
  // credit figures of the portal refresh (they listen to ub:usage-updated)
  const credits = useAiCredits(botId);
  const creditsActive = creditsOn(credits);
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running) window.dispatchEvent(new Event('ub:usage-updated'));
    wasRunning.current = running;
  }, [running]);

  // start page -> conversation: the input glides from below the welcome down to the dock (FLIP)
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const before = last.current;
    last.current = { hero, veiled, top };
    if (before.veiled || !before.hero || hero || before.top == null || typeof el.animate !== 'function') return;
    const dy = before.top - top;
    if (Math.abs(dy) < 4 || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }],
      { duration: 450, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
  });

  // while Mo works the input is blocked (the status sits under the last message); then the cursor is back
  useEffect(() => { if (!running && !veiled) ref.current?.focus(); }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (value, fromComposer) => {
    if (!value || busy || running || !guardWrite()) return;
    setBusy(true);
    setError('');
    try {
      await builder.send(value);
      if (fromComposer) setText('');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
      ref.current?.focus();
    }
  };

  // a starter is an example for another business: it goes into the input to be adapted
  const pickStarter = (k) => {
    setText(t(`agent_studio_starter_${k}_text`));
    setTimeout(() => ref.current?.focus(), 0);
  };

  return (
    <div className={`as-dock ${veiled ? 'is-veiled' : ''}`} ref={box}>
      <BuildFocus builder={builder} />
      {error && <div className="as-error">{t('agent_studio_action_failed', { error })}</div>}
      <StudioComposer
        ref={ref}
        value={text}
        onChange={setText}
        onSubmit={() => send(text.trim(), true)}
        disabled={busy || running}
        autoFocusInput
        placeholder={running ? t('agent_studio_composer_running') : t('agent_studio_composer_ask')}
        placeholders={running || !hero ? null : [t('agent_studio_composer_ask'), ...ideas.slice(IDEA_BUTTONS).map(({ key: k }) => idea(k))]}
        accessory={<MoLevelMenu botId={botId} />}
        voiceUrl={studioUrl(botId, '/transcribe')}
        // Mo's own input stands out (Robert, 18.10.2026): Mo in the field, a slowly moving violet frame with a soft
        // glow, a taller field and a send button that is always strong (agentStudio.css .is-mo)
        className="is-mo"
        leading={<span className="as-mo-avatar">Mo</span>}
      />
      {hero ? (
        <div className="as-hero-chips">
          {showQuestions && (hasAgent
            ? questions.map(({ key: k, icon, text: q }, i) => (
              <button type="button" key={k} className="as-hero-q" style={{ '--d': i }} disabled={busy || running}
                onClick={() => send(q, false)}>
                <FontAwesomeIcon icon={icon} /> {q}
              </button>
            ))
            : STARTERS.map((k, i) => (
              <button type="button" key={k} className="as-hero-q" style={{ '--d': i }} onClick={() => pickStarter(k)}>
                <FontAwesomeIcon icon={faWandMagicSparkles} /> {t('agent_studio_hero_example', { name: t(`agent_studio_starter_${k}`) })}
              </button>
            )))}
        </div>
      ) : (
        <div className={`as-composer-foot ${creditsActive ? 'is-credits' : ''}`}>
          {creditsActive
            ? <span className="as-foot-credits"><CreditsChip botId={botId} /></span>
            : <span>{running ? t('agent_studio_leave_hint') : t('agent_studio_composer_hint')}</span>}
          {canTest && (
            <button type="button" className="as-btn-link" onClick={goTest}>
              <FontAwesomeIcon icon={faFlask} /> {t('agent_studio_test_now')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
